import { BoxGeometry, Mesh, MeshBasicMaterial } from "three";
import { Vector3, Quaternion } from "flavr-math";
import { NewModule } from "@modules/module";
import { modelPool } from "@modules/model";
import { inputActionService } from "@modules/inputs";
import { mainScene } from "@modules/rendering/mainScene";
import { ModelBoundsService } from "@modules/bounds/ModelBoundsService";
import { debugTextService } from "@modules/debug";
const DEBUG_MATERIAL = new MeshBasicMaterial({ color: 0xef016f1 });
/**
 * Updates and maintains model bounding boxes whenever models are added or updated. This module also allows to
 * view these bounding boxes in the scene for debugging purposes.
 */
export class BoundsModule extends NewModule {
    /**
     * Creates the `ModelBoundsService` that tracks and computes bounding boxes for all models.
     */
    constructor() {
        super("BoundsModule");
        // Debugging
        this.showDebugBounds = false;
        this.debugGeometry = new Map();
        this.boundsService = new ModelBoundsService(this.connection);
    }
    /**
     * Registers the developer shortcut that toggles the debug meshes and text overlays for tracked bounding boxes.
     */
    onInit() {
        inputActionService
            .registerAction("ToggleBoundingBoxVisibility", () => {
            this.toggleBoundsVisibility();
        }, "Show and hide model bounding boxes")
            .addKey("KeyM")
            .asDev();
    }
    /// Debug helper methods ///
    toggleBoundsVisibility() {
        this.showDebugBounds = !this.showDebugBounds;
        console.warn(this.boundsService.getAllBoundingBoxes());
        if (!this.showDebugBounds) {
            if (this.debugUpdateHandler !== undefined)
                clearInterval(this.debugUpdateHandler);
            this.hideBounds();
        }
        else {
            if (this.debugUpdateHandler !== undefined)
                return;
            this.debugUpdateHandler = setInterval(this.updateBounds.bind(this), 200);
            this.showBounds();
        }
    }
    updateBounds() {
        for (const uid of this.boundsService.getAvailableModelUIDs()) {
            const bounds = this.boundsService.getBoundingBox(uid);
            if (bounds === undefined)
                continue;
            let mesh = this.debugGeometry.get(uid);
            if (mesh === undefined) {
                mesh = new Mesh(new BoxGeometry(), DEBUG_MATERIAL);
                DEBUG_MATERIAL.wireframe = true;
                this.debugGeometry.set(uid, mesh);
                mainScene.add(mesh);
            }
            bounds.getCenter(mesh.position);
            bounds.getSize(mesh.scale);
        }
    }
    showBounds() {
        const getDebugTextTargets = () => {
            const debugTextTargets = [];
            for (const uid of this.boundsService.getAvailableModelUIDs()) {
                const bounds = this.boundsService.getBoundingBox(uid);
                if (bounds === undefined)
                    continue;
                const model = modelPool.getModel(uid);
                if (model === undefined)
                    continue;
                const centerPosition = bounds.getCenter(new Vector3());
                const anchorPosition = centerPosition.clone();
                anchorPosition.y = bounds.max.y + 0.2;
                debugTextTargets.push({
                    name: model.getName(),
                    uid,
                    box: bounds,
                    position: centerPosition.clone(),
                    centerPosition,
                    anchorPosition,
                    orientation: new Quaternion(),
                });
            }
            return debugTextTargets;
        };
        debugTextService.showDebugText("BoundsModule", getDebugTextTargets);
    }
    hideBounds() {
        for (const mesh of this.debugGeometry.values()) {
            mainScene.remove(mesh);
        }
        this.debugGeometry.clear();
        debugTextService.hideDebugText("BoundsModule");
    }
}
